// MIT License - Copyright (c) 2026 Doug McNall

import React, { useState } from 'react';
import { createPortal } from 'react-dom';
import CrewTrainingModal from './CrewTrainingModal';
import './CrewDetailModal.css';

const CrewDetailModal = ({ crewMember, onClose, getCrewBackstoryInfo, CrewGoalsManager, onTrainingStarted, onFireCrew }) => {
  const [activeTab, setActiveTab] = useState('overview');
  const [actionResult, setActionResult] = useState(null);
  const [showTrainingModal, setShowTrainingModal] = useState(false);

  if (!crewMember) return null;

  const getNextLevelXP = (experience) => {
    const xpLevels = {
      ROOKIE: 1000,
      TRAINED: 2000,
      EXPERIENCED: 3000,
      VETERAN: 5000,
      ELITE: null
    };
    return xpLevels[experience];
  };

  const renderOverview = () => (
    <div className="crew-detail-content">
      <div className="crew-detail-columns">
        {/* Left Column - Portrait & Stats */}
        <div className="crew-detail-left">
          {crewMember.speciesData && (
            <div className="crew-species-card">
              {crewMember.species !== 'human' && (
                <img 
                  src={`/icons/aliens/${crewMember.species}.svg`}
                  alt={crewMember.speciesData.name}
                  className="crew-modal-portrait"
                  onError={(e) => e.target.style.display = 'none'}
                />
              )}
              <div className="crew-species-details">
                <h4>{crewMember.speciesData.name}</h4>
                <p><strong>Origin:</strong> {crewMember.speciesData.origin}</p>
                {crewMember.speciesData.traits && (
                  <>
                    <p><strong>Temperament:</strong> {crewMember.speciesData.traits.temperament}</p>
                    <p><strong>Intelligence:</strong> {crewMember.speciesData.traits.intelligence}</p>
                  </>
                )}
              </div>
            </div>
          )}

          <div className="crew-stats-card">
            <h4>◊ STATISTICS ◊</h4>
            <div className="stat-row">
              <span>Role:</span>
              <span className="stat-value">{crewMember.roleData?.name}</span>
            </div>
            <div className="stat-row">
              <span>Experience:</span>
              <span className="stat-value">{crewMember.experience}</span>
            </div>
            <div className="stat-row">
              <span>Bonus:</span>
              <span className="stat-value">{crewMember.roleData?.bonus}</span>
            </div>
            <div className="stat-row">
              <span>Combat Kills:</span>
              <span className="stat-value">{crewMember.combatKills}</span>
            </div>
            <div className="stat-row">
              <span>Missions:</span>
              <span className="stat-value">{crewMember.missionsCompleted}</span>
            </div>
            
            <div className="xp-section">
              <div className="xp-label">Experience Points</div>
              <div className="xp-bar">
                <div 
                  className="xp-fill"
                  style={{ 
                    width: `${getNextLevelXP(crewMember.experience) ? (crewMember.xp / getNextLevelXP(crewMember.experience) * 100) : 100}%`
                  }}
                />
              </div>
              <div className="xp-text">
                {crewMember.xp} / {getNextLevelXP(crewMember.experience) || 'MAX'}
              </div>
            </div>
          </div>
        </div>

        {/* Right Column - Backstory & Goals */}
        <div className="crew-detail-right">
          {(() => {
            const backstoryInfo = getCrewBackstoryInfo(crewMember.id);
            return backstoryInfo ? (
              <div className="crew-backstory-card">
                <h4>◊ BACKSTORY ◊</h4>
                <div className="backstory-title">
                  <span className="backstory-icon">📖</span>
                  <span>{backstoryInfo.title}</span>
                </div>
                <div className="backstory-summary">{backstoryInfo.summary}</div>
                {backstoryInfo.revealed ? (
                  <div className="backstory-revealed">
                    <div className="backstory-quote">"{backstoryInfo.details}"</div>
                    <div className="backstory-date">
                      Revealed: {new Date(backstoryInfo.revealedAt).toLocaleDateString()}
                    </div>
                  </div>
                ) : (
                  <div className="backstory-locked">
                    <span className="lock-icon">🔒</span>
                    <span>Build relationship to {backstoryInfo.unlockThreshold}% to unlock</span>
                  </div>
                )}
              </div>
            ) : null;
          })()}

          <div className="crew-goals-card">
            <h4>◊ PERSONAL GOALS ◊</h4>
            {(() => {
              const goals = CrewGoalsManager.getCrewGoals(crewMember.id);
              return goals.length > 0 ? (
                <div className="goals-list">
                  {goals.map((goal, index) => (
                    <div key={index} className={`goal-item ${goal.status}`}>
                      <div className="goal-header">
                        <span className="goal-icon">{goal.goalData.icon}</span>
                        <span className="goal-title">{goal.goalData.title}</span>
                      </div>
                      <div className="goal-description">{goal.goalData.description}</div>
                      {goal.status === 'active' && goal.progress !== undefined && (
                        <div className="goal-progress">
                          <div className="goal-progress-bar">
                            <div 
                              className="goal-progress-fill"
                              style={{ width: `${(goal.progress / goal.goalData.target) * 100}%` }}
                            />
                          </div>
                          <span className="goal-progress-text">
                            {goal.progress} / {goal.goalData.target}
                          </span>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              ) : (
                <div className="no-goals">No active goals</div>
              );
            })()}
          </div>
        </div>
      </div>
    </div>
  );

  const getRelationshipColor = (score) => {
    if (score >= 80) return '#00ff88'; // CLOSE - green
    if (score >= 60) return '#00ffff'; // FRIENDLY - cyan
    if (score >= 40) return '#88ccff'; // NEUTRAL - light blue
    if (score >= 20) return '#ffaa00'; // DISTANT - orange
    return '#ff4444'; // HOSTILE - red
  };

  const getRelationshipLabel = (score) => {
    if (score >= 80) return 'CLOSE';
    if (score >= 60) return 'FRIENDLY';
    if (score >= 40) return 'NEUTRAL';
    if (score >= 20) return 'DISTANT';
    return 'HOSTILE';
  };

  const handleMediate = (otherCrewId, otherCrewName) => {
    const CrewRelationshipManager = window.CrewRelationshipManager;
    const result = CrewRelationshipManager.mediateConflict(crewMember.id, otherCrewId);
    setActionResult({ type: result.success ? 'success' : 'warning', message: result.message });
    setTimeout(() => setActionResult(null), 3000);
  };

  const handleCounsel = (otherCrewId, otherCrewName) => {
    const CrewRelationshipManager = window.CrewRelationshipManager;
    const result = CrewRelationshipManager.counselSession(crewMember.id, otherCrewId);
    setActionResult({ type: 'success', message: result.message });
    setTimeout(() => setActionResult(null), 3000);
  };

  const handleTeamBuilding = () => {
    const CrewRelationshipManager = window.CrewRelationshipManager;
    const allCrew = window.CrewManager?.getAllCrew() || [];
    const crewIds = allCrew.map(c => c.id);
    const result = CrewRelationshipManager.teamBuildingActivity(crewIds);
    setActionResult({ type: 'success', message: result.message });
    setTimeout(() => setActionResult(null), 3000);
  };

  const handleRandomEvent = () => {
    const CrewRelationshipManager = window.CrewRelationshipManager;
    const allCrew = window.CrewManager?.getAllCrew() || [];
    const result = CrewRelationshipManager.triggerRandomEvent(allCrew);
    
    if (result.occurred) {
      setActionResult({ type: 'info', message: result.message });
    } else {
      setActionResult({ type: 'info', message: 'No event occurred this time.' });
    }
    setTimeout(() => setActionResult(null), 3000);
  };

  // eslint-disable-next-line no-unused-vars
  const handleTimeDecay = () => {
    const CrewRelationshipManager = window.CrewRelationshipManager;
    const allCrew = window.CrewManager?.getAllCrew() || [];
    const crewIds = allCrew.map(c => c.id);
    const result = CrewRelationshipManager.applyTimeDecay(crewIds);
    setActionResult({ type: 'info', message: `Time decay applied to ${result.affected} relationships.` });
    setTimeout(() => setActionResult(null), 3000);
  };

  const renderRelationships = () => {
    const CrewRelationshipManager = window.CrewRelationshipManager;
    if (!CrewRelationshipManager) {
      return (
        <div className="crew-detail-content">
          <div className="coming-soon">Relationship system not initialized</div>
        </div>
      );
    }

    const allCrew = window.CrewManager?.getAllCrew() || [];
    const crewIds = allCrew.map(c => c.id);
    const dynamics = CrewRelationshipManager.getTeamDynamics(crewIds);
    
    const relationships = allCrew
      .filter(c => c.id !== crewMember.id)
      .map(other => ({
        crew: other,
        score: CrewRelationshipManager.getRelationship(crewMember.id, other.id).score
      }))
      .sort((a, b) => b.score - a.score);

    const getDynamicsColor = (status) => {
      if (status === 'EXCELLENT') return '#00ff88';
      if (status === 'GOOD') return '#00ffff';
      if (status === 'NEUTRAL') return '#88ccff';
      if (status === 'POOR') return '#ffaa00';
      return '#ff4444';
    };

    return (
      <div className="crew-detail-content">
        <div className="relationships-container">
          <div className="relationships-header">
            <h3>◊ {crewMember.name}'s RELATIONSHIPS ◊</h3>
            <div className="header-actions">
              <button 
                className="team-building-button"
                onClick={handleTeamBuilding}
                title="Boost all crew relationships (+5-10)"
              >
                🎯 TEAM BUILDING
              </button>
              <button 
                className="event-button"
                onClick={handleRandomEvent}
                title="Trigger random crew interaction"
              >
                🎲 RANDOM EVENT
              </button>
            </div>
          </div>

          {/* Team Dynamics Summary */}
          <div className="team-dynamics-card">
            <h4>◊ TEAM DYNAMICS ◊</h4>
            <div className="dynamics-grid">
              <div className="dynamics-stat">
                <span className="dynamics-label">Overall Status:</span>
                <span className="dynamics-value" style={{ color: getDynamicsColor(dynamics.status) }}>
                  {dynamics.status}
                </span>
              </div>
              <div className="dynamics-stat">
                <span className="dynamics-label">Average Score:</span>
                <span className="dynamics-value">{dynamics.avgScore}/100</span>
              </div>
              <div className="dynamics-stat">
                <span className="dynamics-label">Combat Bonus:</span>
                <span className="dynamics-value" style={{ color: dynamics.combatBonus >= 1.0 ? '#00ff88' : '#ff4444' }}>
                  {dynamics.combatBonus >= 1.0 ? '+' : ''}{((dynamics.combatBonus - 1) * 100).toFixed(0)}%
                </span>
              </div>
              <div className="dynamics-stat">
                <span className="dynamics-label">Efficiency Bonus:</span>
                <span className="dynamics-value" style={{ color: dynamics.efficiencyBonus >= 1.0 ? '#00ff88' : '#ff4444' }}>
                  {dynamics.efficiencyBonus >= 1.0 ? '+' : ''}{((dynamics.efficiencyBonus - 1) * 100).toFixed(0)}%
                </span>
              </div>
              <div className="dynamics-stat">
                <span className="dynamics-label">Friendships:</span>
                <span className="dynamics-value" style={{ color: '#00ff88' }}>{dynamics.friendships}</span>
              </div>
              <div className="dynamics-stat">
                <span className="dynamics-label">Conflicts:</span>
                <span className="dynamics-value" style={{ color: '#ff4444' }}>{dynamics.conflicts}</span>
              </div>
            </div>
          </div>

          {actionResult && (
            <div className={`action-result ${actionResult.type}`}>
              {actionResult.message}
            </div>
          )}
          
          {relationships.length === 0 ? (
            <div className="no-relationships">No other crew members</div>
          ) : (
            <div className="relationship-grid">
              {relationships.map(({ crew, score }) => (
                <div key={crew.id} className="relationship-row">
                  <div className="relationship-crew-info">
                    <div className="relationship-name">{crew.name}</div>
                    <div className="relationship-role">{crew.roleData?.name}</div>
                  </div>
                  <div className="relationship-score-container">
                    <div className="relationship-bar">
                      <div 
                        className="relationship-fill"
                        style={{ 
                          width: `${score}%`,
                          background: getRelationshipColor(score)
                        }}
                      />
                    </div>
                    <div 
                      className="relationship-label"
                      style={{ color: getRelationshipColor(score) }}
                    >
                      {getRelationshipLabel(score)} ({score})
                    </div>
                  </div>
                  <div className="relationship-actions">
                    {score < 40 && (
                      <button 
                        className="action-button mediate"
                        onClick={() => handleMediate(crew.id, crew.name)}
                        title="Mediate conflict (70-80% success, +10-20)"
                      >
                        🤝 Mediate
                      </button>
                    )}
                    <button 
                      className="action-button counsel"
                      onClick={() => handleCounsel(crew.id, crew.name)}
                      title="Counseling session (+15-25)"
                    >
                      💬 Counsel
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    );
  };

  return createPortal(
    <div className="crew-detail-modal-overlay" onClick={onClose}>
      <div className="crew-detail-modal" onClick={(e) => e.stopPropagation()}>
        <div className="crew-detail-modal-header">
          <h2>◊ {crewMember.name} ◊</h2>
          <button className="crew-detail-close" onClick={onClose}>✕</button>
        </div>

        <div className="crew-detail-tabs">
          <button 
            className={`crew-detail-tab ${activeTab === 'overview' ? 'active' : ''}`}
            onClick={() => setActiveTab('overview')}
          >
            Overview
          </button>
          <button 
            className={`crew-detail-tab ${activeTab === 'relationships' ? 'active' : ''}`}
            onClick={() => setActiveTab('relationships')}
          >
            Relationships
          </button>
          <button 
            className="crew-detail-tab"
            onClick={() => setShowTrainingModal(true)}
          >
            🎓 Training
          </button>
          <button 
            className="crew-detail-tab fire-tab"
            onClick={() => {
              if (window.confirm(`Fire ${crewMember.name}?\n\nThis action cannot be undone.`)) {
                if (onFireCrew) onFireCrew(crewMember.id);
                if (onClose) onClose();
              }
            }}
          >
            🚫 Fire
          </button>
        </div>

        <CrewTrainingModal
          isOpen={showTrainingModal}
          crewMember={crewMember}
          onClose={() => setShowTrainingModal(false)}
          onTrainingStarted={onTrainingStarted}
        />

        <div className="crew-detail-body">
          {activeTab === 'overview' && renderOverview()}
          {activeTab === 'relationships' && renderRelationships()}
        </div>
      </div>
    </div>,
    document.body
  );
};

export default CrewDetailModal;
